import type { ReactNode } from 'react';
import { Input, Label } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';

/**
 * Form Text Field Component
 *
 * Reusable text input field with label and error message.
 * Designed to be used with React Hook Form Controller.
 *
 * @param {FormTextFieldProps} props - Component properties
 * @returns {React.ReactElement} Form text field component
 */

interface FormTextFieldProps {
  field: any;
  label: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  id?: string;
  type?: 'text' | 'email' | 'password' | 'number';
  disabled?: boolean;
  'data-testid'?: string;
  className?: string;
  maxLength?: number;
  /** Rendered beside the input, or beside the label when the line is too narrow to hold both. */
  inputSuffix?: ReactNode;
}

export const FormTextField = ({
  field,
  label,
  placeholder,
  required = false,
  error,
  id,
  type = 'text',
  disabled = false,
  'data-testid': dataTestId,
  className,
  maxLength,
  inputSuffix,
}: FormTextFieldProps): React.ReactElement => {
  const hasSuffix = !!inputSuffix;

  return (
    /*
      A suffix turns the field into one wrapping row: from `sm` up it sits to the right of the
      input, and on a phone it moves up next to the label, where the line is too narrow to hold
      both. Without a suffix the field is the plain stack it has always been.
    */
    <div className={cn('flex gap-1', hasSuffix ? 'flex-wrap items-center gap-x-2 gap-y-1' : 'flex-col')}>
      <Label className={cn({ 'order-1 sm:w-full': hasSuffix, 'text-muted-foreground': disabled })} htmlFor={id}>
        {label} {required && <span className={cn('text-destructive', { 'text-muted-foreground': disabled })}>*</span>}
      </Label>
      {hasSuffix && <div className="order-2 shrink-0 sm:order-3">{inputSuffix}</div>}
      <Input
        {...field}
        className={cn(
          'min-w-0',
          {
            'border-destructive': !!error,
            'order-3 w-full sm:order-2 sm:w-auto sm:flex-1': hasSuffix,
          },
          className,
        )}
        data-testid={dataTestId}
        disabled={disabled}
        id={id}
        maxLength={maxLength}
        placeholder={placeholder}
        type={type}
      />
      {error && <p className={cn('text-xs text-destructive', { 'order-4 w-full': hasSuffix })}>{error}</p>}
    </div>
  );
};
